import { Link, useParams } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import { useApi } from "@/hooks/useApi";
import type { Notice } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";

function formatDate(d?: string): string {
  if (!d) return "";
  return d.slice(0, 10).replace(/-/g, ".");
}

export default function NoticeDetail() {
  const { no } = useParams<{ no: string }>();
  const { data, loading, error, reload } = useApi<Notice | Notice[]>(
    no ? `notices.php?no=${no}` : null
  );

  const notice: Notice | null = Array.isArray(data) ? data[0] ?? null : data;

  return (
    <>
      <PageBanner
        title="공지사항"
        breadcrumb={[{ label: "공지사항", to: "/notices" }, { label: "상세" }]}
      />

      <section className="mx-auto max-w-4xl px-4 md:px-6 py-12 md:py-16">
        {loading ? (
          <div className="rounded-xl border border-background-200 bg-background-50 p-6 md:p-8 space-y-4">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-3 w-40" />
            <div className="pt-4 space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="공지를 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
              >
                다시 시도
              </button>
            }
          />
        ) : !notice ? (
          <EmptyState
            icon="ri-file-unknow-line"
            title="존재하지 않는 공지입니다"
            description="삭제되었거나 잘못된 주소일 수 있습니다."
            action={
              <Link
                to="/notices"
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
              >
                목록으로
              </Link>
            }
          />
        ) : (
          <article className="rounded-xl border border-background-200 bg-background-50 overflow-hidden">
            <header className="border-b border-background-200 p-6 md:p-8">
              <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 leading-snug">
                {notice.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs md:text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" aria-hidden="true"></i>
                  {formatDate(notice.reg_date || notice.date)}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line" aria-hidden="true"></i>
                  조회 {notice.hit ?? 0}
                </span>
              </div>
            </header>
            <div className="p-6 md:p-8">
              <div className="whitespace-pre-wrap break-words text-sm md:text-base leading-relaxed text-foreground-700">
                {notice.content}
              </div>
            </div>
          </article>
        )}

        <div className="mt-8 flex justify-center">
          <Link
            to="/notices"
            className="whitespace-nowrap inline-flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100 transition-colors"
          >
            <i className="ri-list-check" aria-hidden="true"></i>
            목록으로
          </Link>
        </div>
      </section>
    </>
  );
}